import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';

export default class Enemy {
    constructor(scene, config, position) {
        this.scene = scene;
        this.config = config;
        this.mesh = null;
        this.mixer = null;
        this.isAlive = true;
        this.isDying = false;
        this.isLoaded = false;

        // Stats from config
        this.health = config.health;
        this.maxHealth = config.health;
        this.speed = config.speed;

        // AI state
        this.state = 'idle'; // idle, approach, orbit
        this.orbitAngle = Math.random() * Math.PI * 2;
        this.orbitCenter = position.clone();
        this.bobPhase = Math.random() * Math.PI * 2;
        this.attackTimer = config.attackCooldown;

        // Beam attack state
        this.isBeaming = false;
        this.beamTimer = 0;
        this.beamDamageTimer = 0;
        this.beamMesh = null;
        this.beamLight = null;

        // Departure state — UFO flies up into the sky and disappears
        this.isDeparting = false;
        this.isDeparted = false;
        this.departTimer = 0;
        this.departDuration = 3.0; // seconds to fly up and vanish
        this.departDirection = new THREE.Vector3(
            (Math.random() - 0.5) * 0.3,
            1,
            (Math.random() - 0.5) * 0.3
        ).normalize();

        // Visual feedback
        this.hitFlashTimer = 0;
        this.deathTimer = 0;
        this.deathDuration = 0.3;
        this.originalMaterials = [];

        // Spawn position
        this.spawnPosition = position.clone();

        // Load model
        this.loadModel(position);
    }

    loadModel(position) {
        const loader = new GLTFLoader();
        loader.load(
            this.config.model,
            (glb) => {
                this.mesh = glb.scene;
                const s = this.config.scale;
                this.mesh.scale.set(s, s, s);
                this.mesh.position.copy(position);
                this.mesh.userData.enemy = this;

                this.mesh.traverse((child) => {
                    if (child.isMesh) {
                        child.castShadow = true;
                        child.receiveShadow = true;
                        this.originalMaterials.push({
                            mesh: child,
                            material: child.material.clone()
                        });
                    }
                });

                if (glb.animations && glb.animations.length > 0) {
                    this.mixer = new THREE.AnimationMixer(this.mesh);
                    const action = this.mixer.clipAction(glb.animations[0]);
                    action.play();
                }

                this.scene.add(this.mesh);
                this.isLoaded = true;
            },
            undefined,
            (error) => {
                console.error(`[Enemy] Failed to load model ${this.config.model}:`, error);
                this.createFallbackMesh(position);
            }
        );
    }

    createFallbackMesh(position) {
        const geometry = new THREE.SphereGeometry(1.5, 8, 6);
        const material = new THREE.MeshLambertMaterial({ color: 0x66ff66 });
        this.mesh = new THREE.Mesh(geometry, material);
        this.mesh.position.copy(position);
        this.mesh.userData.enemy = this;
        this.mesh.castShadow = true;
        this.scene.add(this.mesh);
        this.isLoaded = true;
    }

    update(deltaTime, busPosition) {
        if (!this.mesh || !this.isAlive || !this.isLoaded) return;

        // Handle dying animation
        if (this.isDying) {
            this.deathTimer += deltaTime;
            const t = this.deathTimer / this.deathDuration;
            const s = this.config.scale * Math.max(0, 1 - t);
            this.mesh.scale.set(s, s, s);
            if (t >= 1) {
                this.isAlive = false;
                this.scene.remove(this.mesh);
            }
            return;
        }

        // Handle departure — fly up into the sky and vanish
        if (this.isDeparting) {
            this.updateDeparture(deltaTime);
            return;
        }

        // Update animation mixer
        if (this.mixer) {
            this.mixer.update(deltaTime);
        }

        // Update hit flash
        if (this.hitFlashTimer > 0) {
            this.hitFlashTimer -= deltaTime;
            if (this.hitFlashTimer <= 0) {
                this.restoreMaterials();
            }
        }

        // Dispatch to behavior
        const behavior = this.config.behavior;
        if (behavior === 'fly_circle') {
            this.updateFlyCircle(deltaTime, busPosition);
        } else if (behavior === 'fly_hover') {
            this.updateFlyHover(deltaTime, busPosition);
        }
    }

    // ── Fly Circle Behavior ─────────────────────────────────────────────

    updateFlyCircle(deltaTime, busPosition) {
        const distToBus = this.mesh.position.distanceTo(busPosition);

        // State transitions
        if (distToBus <= this.config.attackRange) {
            this.state = 'orbit';
        } else if (distToBus <= this.config.detectionRange) {
            this.state = 'approach';
        } else {
            this.state = 'idle';
        }

        // Get terrain height at current position
        const heightMap = this.scene?.userData?.terrainHeightMap;
        const terrainY = heightMap
            ? heightMap.getHeight(this.mesh.position.x, this.mesh.position.z)
            : 0;
        const targetY = terrainY + this.config.flyHeight;

        switch (this.state) {
            case 'idle':
                this.updateIdlePatrol(deltaTime, targetY);
                break;
            case 'approach':
                this.updateApproach(deltaTime, busPosition, targetY);
                break;
            case 'orbit':
                this.updateOrbit(deltaTime, busPosition, targetY);
                break;
        }

        // Beam attack logic (while orbiting)
        if (this.state === 'orbit') {
            if (this.isBeaming) {
                this.updateBeam(deltaTime, busPosition);
            } else {
                this.attackTimer -= deltaTime;
                if (this.attackTimer <= 0) {
                    this.startBeam(busPosition);
                }
            }
        } else {
            // Stop beam if UFO leaves orbit range
            if (this.isBeaming) {
                this.stopBeam();
            }
        }

        // Always face movement direction (yaw only)
        this.faceMovementDirection(busPosition);

        // Gentle tilt/wobble for visual life
        this.bobPhase += deltaTime * this.config.bobFrequency * Math.PI * 2;
        this.mesh.rotation.z = Math.sin(this.bobPhase * 0.7) * 0.1;
    }

    updateIdlePatrol(deltaTime, targetY) {
        // Fly in wide circle around spawn position
        this.orbitAngle += deltaTime * this.speed * 0.01;
        const radius = 60;
        const targetX = this.orbitCenter.x + Math.cos(this.orbitAngle) * radius;
        const targetZ = this.orbitCenter.z + Math.sin(this.orbitAngle) * radius;

        // Bob up and down
        const bob = Math.sin(this.bobPhase) * this.config.bobAmplitude;

        this.mesh.position.x += (targetX - this.mesh.position.x) * deltaTime * 2;
        this.mesh.position.z += (targetZ - this.mesh.position.z) * deltaTime * 2;
        this.mesh.position.y += (targetY + bob - this.mesh.position.y) * deltaTime * 3;
    }

    updateApproach(deltaTime, busPosition, targetY) {
        // Move orbit center toward bus
        this.orbitCenter.x += (busPosition.x - this.orbitCenter.x) * deltaTime * 0.5;
        this.orbitCenter.z += (busPosition.z - this.orbitCenter.z) * deltaTime * 0.5;

        // Continue circling but around the moving center
        this.orbitAngle += deltaTime * this.speed * 0.02;
        const radius = this.config.orbitRadius * 1.5;
        const targetX = this.orbitCenter.x + Math.cos(this.orbitAngle) * radius;
        const targetZ = this.orbitCenter.z + Math.sin(this.orbitAngle) * radius;

        const bob = Math.sin(this.bobPhase) * this.config.bobAmplitude;

        this.mesh.position.x += (targetX - this.mesh.position.x) * deltaTime * 3;
        this.mesh.position.z += (targetZ - this.mesh.position.z) * deltaTime * 3;
        this.mesh.position.y += (targetY + bob - this.mesh.position.y) * deltaTime * 3;
    }

    updateOrbit(deltaTime, busPosition, targetY) {
        // Tight orbit around bus
        this.orbitCenter.x += (busPosition.x - this.orbitCenter.x) * deltaTime * 2;
        this.orbitCenter.z += (busPosition.z - this.orbitCenter.z) * deltaTime * 2;

        this.orbitAngle += deltaTime * this.speed * 0.03;
        const radius = this.config.orbitRadius;
        const targetX = this.orbitCenter.x + Math.cos(this.orbitAngle) * radius;
        const targetZ = this.orbitCenter.z + Math.sin(this.orbitAngle) * radius;

        const bob = Math.sin(this.bobPhase) * this.config.bobAmplitude;

        this.mesh.position.x += (targetX - this.mesh.position.x) * deltaTime * 4;
        this.mesh.position.z += (targetZ - this.mesh.position.z) * deltaTime * 4;
        this.mesh.position.y += (targetY + bob - this.mesh.position.y) * deltaTime * 3;
    }

    // ── Beam Attack ─────────────────────────────────────────────────────

    startBeam(busPosition) {
        this.isBeaming = true;
        this.beamTimer = 0;
        this.beamDamageTimer = 0;
        this.attackTimer = this.config.attackCooldown;

        // Create beam cone — wide at bottom, narrow at top (UFO)
        const beamHeight = this.mesh.position.y - (busPosition.y || 0);
        const beamRadius = this.config.beamRadius || 4;
        const beamGeo = new THREE.ConeGeometry(beamRadius, beamHeight, 16, 1, true);

        // Shift geometry so the tip (narrow end) is at origin (UFO position)
        beamGeo.translate(0, -beamHeight / 2, 0);

        const beamMat = new THREE.MeshBasicMaterial({
            color: this.config.beamColor || 0x66ff66,
            transparent: true,
            opacity: 0.18,
            side: THREE.DoubleSide,
            depthWrite: false,
        });

        this.beamMesh = new THREE.Mesh(beamGeo, beamMat);
        this.beamMesh.position.copy(this.mesh.position);
        this.scene.add(this.beamMesh);

        // Add a point light at the beam base for glow on ground
        this.beamLight = new THREE.PointLight(
            this.config.beamColor || 0x66ff66,
            2, 30
        );
        this.beamLight.position.set(
            this.mesh.position.x,
            busPosition.y + 1,
            this.mesh.position.z
        );
        this.scene.add(this.beamLight);
    }

    updateBeam(deltaTime, busPosition) {
        this.beamTimer += deltaTime;
        const beamDuration = this.config.beamDuration || 3.0;

        // Fade in/out
        const fadeIn = Math.min(this.beamTimer / 0.3, 1);
        const fadeOut = Math.max(0, 1 - (this.beamTimer - (beamDuration - 0.3)) / 0.3);
        const opacity = 0.22 * fadeIn * (this.beamTimer > beamDuration - 0.3 ? fadeOut : 1);

        if (this.beamMesh) {
            // Update beam position to follow UFO
            this.beamMesh.position.copy(this.mesh.position);

            // Rebuild geometry if height changed significantly
            const beamHeight = this.mesh.position.y - (busPosition.y || 0);
            if (beamHeight > 2) {
                this.beamMesh.geometry.dispose();
                const newGeo = new THREE.ConeGeometry(
                    this.config.beamRadius || 4, beamHeight, 16, 1, true
                );
                newGeo.translate(0, -beamHeight / 2, 0);
                this.beamMesh.geometry = newGeo;
            }

            this.beamMesh.material.opacity = opacity;

            // Pulse effect — slight scale oscillation
            const pulse = 1 + Math.sin(this.beamTimer * 8) * 0.05;
            this.beamMesh.scale.set(pulse, 1, pulse);
        }

        if (this.beamLight) {
            this.beamLight.position.set(
                this.mesh.position.x,
                busPosition.y + 1,
                this.mesh.position.z
            );
            this.beamLight.intensity = 2 * opacity / 0.22;
        }

        // Drain fuel periodically while beam is active
        this.beamDamageTimer -= deltaTime;
        if (this.beamDamageTimer <= 0) {
            this.beamDamageTimer = this.config.beamDamageInterval || 0.5;
            // Only deal damage if UFO is roughly above the bus
            const dx = this.mesh.position.x - busPosition.x;
            const dz = this.mesh.position.z - busPosition.z;
            const horizDist = Math.sqrt(dx * dx + dz * dz);
            if (horizDist < (this.config.beamRadius || 4) + 8) {
                window.dispatchEvent(new CustomEvent('enemy_attack', {
                    detail: { fuelDamage: this.config.fuelDamage, attacker: this }
                }));
            }
        }

        // End beam after duration
        if (this.beamTimer >= beamDuration) {
            this.stopBeam();
        }
    }

    stopBeam() {
        this.isBeaming = false;
        if (this.beamMesh) {
            this.scene.remove(this.beamMesh);
            this.beamMesh.geometry.dispose();
            this.beamMesh.material.dispose();
            this.beamMesh = null;
        }
        if (this.beamLight) {
            this.scene.remove(this.beamLight);
            this.beamLight.dispose();
            this.beamLight = null;
        }
    }

    // ── Fly Hover Behavior ──────────────────────────────────────────────

    updateFlyHover(deltaTime, busPosition) {
        const heightMap = this.scene?.userData?.terrainHeightMap;
        const terrainY = heightMap
            ? heightMap.getHeight(this.mesh.position.x, this.mesh.position.z)
            : 0;
        const targetY = terrainY + this.config.flyHeight;

        // Hover near bus, strafing side to side
        const distToBus = this.mesh.position.distanceTo(busPosition);
        if (distToBus > this.config.detectionRange) {
            // Idle hover at spawn
            const bob = Math.sin(this.bobPhase) * this.config.bobAmplitude;
            this.mesh.position.y += (targetY + bob - this.mesh.position.y) * deltaTime * 3;
            this.bobPhase += deltaTime * this.config.bobFrequency * Math.PI * 2;
            return;
        }

        // Strafe around bus
        this.orbitAngle += deltaTime * this.speed * 0.02;
        const strafeX = busPosition.x + Math.cos(this.orbitAngle) * this.config.orbitRadius;
        const strafeZ = busPosition.z + Math.sin(this.orbitAngle) * this.config.orbitRadius;
        const bob = Math.sin(this.bobPhase) * this.config.bobAmplitude;

        this.mesh.position.x += (strafeX - this.mesh.position.x) * deltaTime * 3;
        this.mesh.position.z += (strafeZ - this.mesh.position.z) * deltaTime * 3;
        this.mesh.position.y += (targetY + bob - this.mesh.position.y) * deltaTime * 3;

        this.bobPhase += deltaTime * this.config.bobFrequency * Math.PI * 2;
        this.faceMovementDirection(busPosition);

        // Attack
        this.attackTimer -= deltaTime;
        if (this.attackTimer <= 0 && distToBus <= this.config.attackRange * 2) {
            this.attackTimer = this.config.attackCooldown;
            window.dispatchEvent(new CustomEvent('enemy_attack', {
                detail: { fuelDamage: this.config.fuelDamage, attacker: this }
            }));
        }
    }

    // ── Shared Helpers ──────────────────────────────────────────────────

    faceMovementDirection(target) {
        if (!this.mesh) return;
        const dx = target.x - this.mesh.position.x;
        const dz = target.z - this.mesh.position.z;
        if (dx * dx + dz * dz > 1) {
            this.mesh.rotation.y = Math.atan2(dx, dz);
        }
    }

    hit(damage) {
        if (!this.isAlive || this.isDying || this.isDeparting) return;
        this.health -= damage;
        this.flashRed();
        if (this.health <= 0) {
            this.die();
        }
    }

    flashRed() {
        this.hitFlashTimer = 0.15;
        this.mesh.traverse((child) => {
            if (child.isMesh) {
                child.material = new THREE.MeshLambertMaterial({ color: 0xff0000 });
            }
        });
    }

    restoreMaterials() {
        for (const entry of this.originalMaterials) {
            entry.mesh.material = entry.material.clone();
        }
    }

    die() {
        this.isDying = true;
        this.deathTimer = 0;
        window.dispatchEvent(new CustomEvent('enemy_killed', {
            detail: {
                enemy: this,
                position: this.mesh.position.clone(),
                scoreValue: this.config.scoreValue
            }
        }));
    }

    // ── Departure ─────────────────────────────────────────────────────

    /**
     * Start departure — UFO stops attacking and flies up into the sky.
     */
    depart() {
        if (this.isDeparting || this.isDying) return;
        this.isDeparting = true;
        this.departTimer = 0;
        this.stopBeam();
    }

    updateDeparture(deltaTime) {
        this.departTimer += deltaTime;
        const t = this.departTimer / this.departDuration;

        // Accelerate upward (starts slow, gets faster)
        const flySpeed = 20 + t * 80;
        this.mesh.position.x += this.departDirection.x * flySpeed * deltaTime;
        this.mesh.position.y += this.departDirection.y * flySpeed * deltaTime;
        this.mesh.position.z += this.departDirection.z * flySpeed * deltaTime;

        // Fade out by scaling down in the last third
        if (t > 0.6) {
            const fadeT = (t - 0.6) / 0.4; // 0→1 over last 40%
            const s = this.config.scale * Math.max(0, 1 - fadeT);
            this.mesh.scale.set(s, s, s);
        }

        // Spin faster as it leaves
        this.mesh.rotation.y += deltaTime * (3 + t * 10);

        // Update mixer for animations
        if (this.mixer) this.mixer.update(deltaTime);

        // Done — mark as departed for removal
        if (t >= 1) {
            this.isDeparted = true;
            this.scene.remove(this.mesh);
        }
    }

    dispose() {
        this.stopBeam();
        if (this.mesh) {
            this.scene.remove(this.mesh);
            this.mesh.traverse((child) => {
                if (child.isMesh) {
                    child.geometry?.dispose();
                    child.material?.dispose();
                }
            });
            this.mesh = null;
        }
        if (this.mixer) {
            this.mixer.stopAllAction();
            this.mixer = null;
        }
        this.isAlive = false;
    }
}
